import React, { useMemo, useState } from "react";
import { Icon } from "@/components/Icon";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBetSlip } from "@/context/BetSlipContext";
import { formatOdds, useOddsFormat } from "@/lib/odds";
import { classifyLeg, workingOdds } from "@/lib/slipOdds";
import { usePoller } from "@/lib/usePoller";
import { EmptyBody } from "@/components/GlobalBetSlip";
import Keypad from "../ui/Keypad";
import { useMobilePrefs } from "../ui/MobilePrefs";

/* BetSlipSheetV2 (design 3c + 3d) — the DESIGNED mobile bet-slip sheet.
   Singles/Parlay toggle, pick rows with inline moved-price flag + Accept gate,
   a STAKE field driven by the custom numeric Keypad (stake/payout/Place always
   above the keys), quick-stake chips, and the Place CTA.

   It places REAL bets through the SAME money-path as GlobalBetSlip's SlipBody:
   the /odds/quote poll + classifyLeg/workingOdds price logic, and POST /parlays /
   POST /bets. No placement logic is duplicated insecurely — the server stays the
   sole authority (accepted_odds / accepted_line / accepted_version / accept_odds_
   change are all sent exactly as the desktop slip sends them), a LINE change can
   never be placed as-is, and a changed PRICE must be explicitly Accepted first. */

const MARKET_UP = { moneyline: "Moneyline", spread: "Spread", total: "Total", prop: "Prop" };
const fmtCoins = (n) => (Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtCash = (n) => `$${(Number(n) || 0).toFixed(2)}`;

export default function BetSlipSheetV2({ close }) {
  const { legs, remove, clear, setOdds } = useBetSlip();
  const { user, refresh } = useAuth();
  const oddsFmt = useOddsFormat();
  const prefs = useMobilePrefs();
  const currency = prefs?.isCash ? "cash" : "coins"; // drives tint + which balance is spent
  const isCash = currency === "cash";

  const [stake, setStake] = useState("");
  const [mode, setMode] = useState("parlay"); // "parlay" | "singles" (only meaningful with 2+ legs)
  const [placing, setPlacing] = useState(false);
  const [quotes, setQuotes] = useState({});

  // live-refresh authoritative quotes every 1s (identical cadence to the desktop slip). Keeps the
  // last-good quote on a stall; never loosens a placement gate.
  const quoteKey = legs.map((l) => `${l.eventId}:${l.outcomeId}`).join(",");
  usePoller(async () => {
    if (legs.length === 0) return;
    const { data } = await api.post("/odds/quote", { legs: legs.map((l) => ({ event_id: l.eventId, outcome_id: l.outcomeId })) });
    const m = {};
    data.forEach((q) => {
      m[`${q.event_id}:${q.outcome_id}`] = {
        odds: q.odds, available: q.available, reason: q.reason, marketVersion: q.market_version,
        line: q.line, label: q.label, side: q.side, market: q.market,
        receivedAt: q.received_at, providerUpdatedAt: q.provider_updated_at, expiresAt: q.expires_at,
      };
    });
    setQuotes(m);
  }, { interval: 1000, staleAfter: 6000, enabled: legs.length > 0, deps: [quoteKey] });

  const qkey = (l) => `${l.eventId}:${l.outcomeId}`;
  const states = {};
  legs.forEach((l) => { states[qkey(l)] = classifyLeg(l, quotes[qkey(l)]); });
  const moved = legs.filter((l) => states[qkey(l)].priceOnly);
  const lineMoved = legs.filter((l) => states[qkey(l)].lineChanged);
  const unavailable = legs.filter((l) => states[qkey(l)].unavail);
  // This designed sheet uses an explicit Accept gate (3d) — never silent auto-accept.
  const wOdds = (l) => workingOdds(l, quotes[qkey(l)], false);

  const applyQuotes = (list) => {
    const u = {};
    list.forEach((l) => {
      const q = quotes[qkey(l)];
      if (!q) return;
      u[qkey(l)] = { odds: q.odds, marketVersion: q.marketVersion, line: q.line, label: q.label, side: q.side, market: q.market, receivedAt: q.receivedAt, providerUpdatedAt: q.providerUpdatedAt, expiresAt: q.expiresAt };
    });
    setOdds(u);
  };

  const multi = legs.length >= 2;
  const effMode = multi ? mode : "singles";
  const isParlay = multi && effMode === "parlay";
  const combined = legs.reduce((a, l) => a * wOdds(l), 1);

  const bal = isCash ? (user?.cash || 0) : (user?.coins || 0);
  const curLabel = isCash ? "PCash" : "PCoins";
  const fmtAmt = isCash ? fmtCash : fmtCoins;
  const stakeNum = stake ? parseFloat(stake) : 0;

  // payout: parlay = stake × combined; singles = Σ stake × each leg (stake per leg)
  let totalReturn = 0;
  if (stakeNum) {
    if (isParlay) totalReturn = stakeNum * combined;
    else totalReturn = legs.reduce((a, l) => a + stakeNum * wOdds(l), 0);
  }
  const totalStake = stakeNum * (isParlay ? 1 : legs.length);

  const setMax = () => {
    const divisor = (multi && !isParlay) ? legs.length : 1;
    const perMax = bal / divisor;
    const v = isCash ? Math.floor(perMax * 100) / 100 : Math.floor(perMax);
    setStake(String(v > 0 ? v : 0));
  };

  const blocked = placing || unavailable.length > 0 || lineMoved.length > 0 || moved.length > 0 || !stakeNum || stakeNum <= 0;

  const place = async () => {
    if (legs.length === 0) return;
    if (lineMoved.length > 0) { toast.error("A line changed — review the updated pick before placing."); return; }
    if (moved.length > 0) { toast.error("A price moved — Accept the new price before placing."); return; }
    const amt = parseFloat(stake);
    if (!amt || amt <= 0) { toast.error("Enter a valid stake"); return; }
    const needed = amt * (isParlay ? 1 : legs.length);
    if (needed > bal) { toast.error(`Insufficient ${curLabel} balance`); return; }
    setPlacing(true);
    try {
      if (isParlay) {
        await api.post("/parlays", {
          legs: legs.map((l) => ({ event_id: l.eventId, outcome_id: l.outcomeId, accepted_odds: l.odds, accepted_line: l.line ?? null, accepted_version: l.marketVersion ?? null })),
          stake: amt, currency, accept_odds_change: false,
          accepted_odds: Math.round(legs.reduce((a, l) => a * (Number(l.odds) || 1), 1) * 100) / 100,
        });
        toast.success("Parlay placed!");
      } else if (legs.length === 1) {
        const l = legs[0];
        await api.post("/bets", { event_id: l.eventId, outcome_id: l.outcomeId, stake: amt, currency, accepted_odds: l.odds, accepted_line: l.line ?? null, accepted_version: l.marketVersion ?? null, accept_odds_change: false });
        toast.success("Bet placed!");
      } else {
        // Multi-singles: place each; keep per-leg failures IN the slip (re-priced
        // when the server reports a moved price) instead of wiping everything.
        const placed = [];
        const failed = [];
        for (const l of legs) {
          try {
            await api.post("/bets", { event_id: l.eventId, outcome_id: l.outcomeId, stake: amt, currency, accepted_odds: l.odds, accepted_line: l.line ?? null, accepted_version: l.marketVersion ?? null, accept_odds_change: false });
            placed.push(l);
          } catch (err) {
            failed.push(l);
            const d = err.response?.data?.detail;
            if (err.response?.status === 409 && (d?.code === "odds_changed" || d?.code === "quote_refresh_required") && d?.current_odds != null) {
              const key = d?.outcome_id ? `${d.event_id}:${d.outcome_id}` : qkey(l);
              setOdds({ [key]: { odds: d.current_odds, marketVersion: d.current_version, line: d.current_line, label: d.current_label, side: d.current_side, market: d.current_market, receivedAt: d.received_at, providerUpdatedAt: d.provider_updated_at, expiresAt: d.expires_at } });
            }
          }
        }
        if (placed.length && failed.length) {
          // Remove only the bets that actually placed; leave the rest (now re-priced)
          // so the user can Accept/adjust and place them — never discard picks.
          placed.forEach((l) => remove(l.eventId, l.outcomeId));
          toast.message(`${placed.length} placed · ${failed.length} need review (price or balance).`);
          setPlacing(false);
          await refresh();
          return; // keep the sheet open for the remaining picks
        }
        if (!placed.length) { toast.error("Could not place bets — check balance and prices."); setPlacing(false); return; }
        toast.success(`${placed.length} single bets placed!`);
      }
      clear(); setStake("");
      await refresh();
      close && close();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d?.code === "line_changed") {
        toast.message(d.message || "A line changed — review the updated pick before placing.");
      } else if (e.response?.status === 409 && (d?.code === "odds_changed" || d?.code === "quote_refresh_required")) {
        // pull the fresh quote in so the moved banner + Accept gate light up
        const l = d?.event_id && d?.outcome_id ? legs.find((leg) => leg.eventId === d.event_id && String(leg.outcomeId) === String(d.outcome_id)) : legs[0];
        if (l && d?.current_odds != null) {
          setOdds({ [qkey(l)]: { odds: d.current_odds, marketVersion: d.current_version, line: d.current_line, label: d.current_label, side: d.current_side, market: d.current_market, receivedAt: d.received_at, providerUpdatedAt: d.provider_updated_at, expiresAt: d.expires_at } });
        }
        toast.message(d.message || "Market refreshed — accept the current price.");
      } else {
        toast.error(apiError(d) || d?.message || e.message);
      }
    } finally { setPlacing(false); }
  };

  const placeLabel = placing ? "Placing…"
    : isParlay ? "Place parlay"
    : legs.length > 1 ? `Place ${legs.length} bets`
    : "Place bet";

  const quickChips = isCash ? ["+$1", "+$5", "+$10", "Max"] : ["+100", "+500", "+1K", "Max"];

  if (legs.length === 0) {
    return (
      <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <EmptyBody />
      </div>
    );
  }

  return (
    <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {/* header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "4px 16px 10px" }}>
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ font: "800 20px 'Manrope'", letterSpacing: "-.02em" }}>Bet slip</span>
          <span className="m-tnum" style={{ minWidth: 22, height: 22, padding: "0 6px", borderRadius: 999, background: "#F4F4F5", color: "#0A0A0A", display: "flex", alignItems: "center", justifyContent: "center", font: "800 12px 'Outfit'" }}>{legs.length}</span>
        </span>
        <button type="button" onClick={clear} style={{ background: "none", border: 0, cursor: "pointer", font: "600 13px 'Outfit'", color: "#71717a" }}>Clear all</button>
      </div>

      {/* scrollable body */}
      <div className="m-no-scrollbar" style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "0 16px 8px" }}>
        {/* Singles / Parlay segmented */}
        {multi && (
          <div style={{ display: "flex", height: 44, borderRadius: 14, padding: 4, marginBottom: 12, background: "#0E0E11", boxShadow: "inset 0 0 0 1px #1F1F23,inset 0 1px 2px rgba(0,0,0,.4)" }}>
            <button type="button" onClick={() => setMode("singles")}
              style={{ flex: 1, borderRadius: 10, border: 0, cursor: "pointer", font: "600 14px 'Outfit'", color: effMode === "singles" ? "#fff" : "#71717a",
                background: effMode === "singles" ? "linear-gradient(180deg,#2A2A30,#202025)" : "transparent", boxShadow: effMode === "singles" ? "inset 0 1px 0 rgba(255,255,255,.1),0 2px 6px rgba(0,0,0,.4)" : "none" }}>
              Singles
            </button>
            <button type="button" onClick={() => setMode("parlay")}
              style={{ flex: 1, borderRadius: 10, border: 0, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 6, font: "600 14px 'Outfit'", color: effMode === "parlay" ? "#fff" : "#71717a",
                background: effMode === "parlay" ? "linear-gradient(180deg,#2A2A30,#202025)" : "transparent", boxShadow: effMode === "parlay" ? "inset 0 1px 0 rgba(255,255,255,.1),0 2px 6px rgba(0,0,0,.4)" : "none" }}>
              Parlay {isParlay && <span className="m-tnum" style={{ font: "800 12px 'Manrope'", color: "#00D632" }}>{formatOdds(combined, oddsFmt)}</span>}
            </button>
          </div>
        )}

        {/* legs */}
        <div style={{ borderRadius: 16, overflow: "hidden", background: "#141417", boxShadow: "inset 0 0 0 1px rgba(255,255,255,.05)" }}>
          {legs.map((l, idx) => {
            const s = states[qkey(l)];
            const q = s?.q;
            const isMoved = s?.priceOnly && q && q.odds != null;
            const up = isMoved && q.odds > l.odds;
            const isUnavail = s?.unavail;
            const isLineChg = s?.lineChanged;
            return (
              <div key={qkey(l)} style={{ padding: "12px 14px", borderTop: idx > 0 ? "1px solid #1A1A1E" : "none", background: isUnavail ? "rgba(239,68,68,.05)" : "transparent" }}>
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}>
                  <div style={{ minWidth: 0 }}>
                    <p style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", font: "700 14px 'Outfit'", color: isUnavail ? "#71717a" : "#fff" }}>{l.label}</p>
                    <p style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", font: "500 11.5px 'Outfit'", color: "#71717a", marginTop: 2 }}>
                      {[MARKET_UP[l.market] || "Pick", l.eventLabel].filter(Boolean).join(" · ")}
                    </p>
                  </div>
                  <span style={{ display: "flex", alignItems: "center", gap: 6, flex: "none" }}>
                    {isMoved && <span className="m-tnum" style={{ font: "600 12px 'Outfit'", color: "#52525b", textDecoration: "line-through" }}>{formatOdds(l.odds, oddsFmt)}</span>}
                    <span className="m-tnum" style={{ font: "800 15px 'Manrope'", color: isMoved ? (up ? "#00D632" : "#EF4444") : "#fff" }}>{formatOdds(isMoved ? q.odds : l.odds, oddsFmt)}</span>
                    <button type="button" aria-label="Remove pick" onClick={() => remove(l.eventId, l.outcomeId)} style={{ width: 30, height: 30, flex: "none", borderRadius: 8, border: 0, cursor: "pointer", background: "none", color: "#52525b", display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <Icon name="x" size={14} />
                    </button>
                  </span>
                </div>
                {isUnavail && <span style={{ display: "inline-block", marginTop: 6, padding: "2px 7px", borderRadius: 6, font: "800 10px 'Outfit'", letterSpacing: ".04em", textTransform: "uppercase", background: "rgba(239,68,68,.12)", color: "#EF4444" }}>Unavailable</span>}
                {isLineChg && !isUnavail && <span style={{ display: "inline-block", marginTop: 6, padding: "2px 7px", borderRadius: 6, font: "800 10px 'Outfit'", letterSpacing: ".04em", textTransform: "uppercase", background: "rgba(239,68,68,.12)", color: "#EF4444" }}>Line changed → {q?.line != null ? q.line : "new line"}</span>}
              </div>
            );
          })}
        </div>

        {/* 3d: odds-changed banner with Accept gate */}
        {moved.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, height: 52, borderRadius: 14, padding: "0 12px", marginTop: 10, background: "#1C1C21", boxShadow: "inset 0 0 0 1px #2F2F35" }}>
            <span style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
              <Icon name="trending-down" size={16} style={{ color: "#EF4444" }} />
              <span style={{ font: "600 13px 'Outfit'", color: "#fff" }}>Odds changed on {moved.length} pick{moved.length > 1 ? "s" : ""}</span>
            </span>
            <button type="button" onClick={() => applyQuotes(moved)} style={{ flex: "none", height: 34, borderRadius: 10, padding: "0 14px", border: 0, cursor: "pointer", background: "#F4F4F5", color: "#0A0A0A", font: "800 14px 'Outfit'" }}>Accept</button>
          </div>
        )}
        {/* line-changed banner (always needs Update) */}
        {lineMoved.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, minHeight: 52, borderRadius: 14, padding: "8px 12px", marginTop: 10, background: "rgba(239,68,68,.10)", boxShadow: "inset 0 0 0 1px rgba(239,68,68,.35)" }}>
            <span style={{ font: "600 13px 'Outfit'", color: "#fff", minWidth: 0 }}>Line changed on {lineMoved.length} pick{lineMoved.length > 1 ? "s" : ""} — review before placing</span>
            <button type="button" onClick={() => applyQuotes(lineMoved)} style={{ flex: "none", height: 34, borderRadius: 10, padding: "0 14px", border: 0, cursor: "pointer", background: "#F4F4F5", color: "#0A0A0A", font: "800 14px 'Outfit'" }}>Update</button>
          </div>
        )}
        {unavailable.length > 0 && (
          <p style={{ marginTop: 10, borderRadius: 14, padding: "10px 12px", font: "600 12px 'Outfit'", color: "#EF4444", background: "rgba(239,68,68,.12)", boxShadow: "inset 0 0 0 1px rgba(239,68,68,.32)" }}>
            {unavailable.length} pick{unavailable.length > 1 ? "s are" : " is"} no longer available — remove {unavailable.length > 1 ? "them" : "it"} to continue.
          </p>
        )}

        {/* stake field — payout always shown above the keys */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, height: 60, borderRadius: 16, padding: "0 14px", marginTop: 12, background: "#0E0E11", boxShadow: "inset 0 0 0 1.5px #F4F4F5" }}>
          <Icon name={isCash ? "pnrcash" : "pnrcoin"} size={18} style={{ color: isCash ? "#E4E4E7" : "#00D632" }} />
          <div style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0 }}>
            <span style={{ font: "600 10px 'Outfit'", letterSpacing: ".1em", textTransform: "uppercase", color: "#71717a" }}>{multi && !isParlay ? `Stake per bet · ${curLabel}` : `Stake · ${curLabel}`}</span>
            <span style={{ display: "flex", alignItems: "center", gap: 2 }}>
              <span className="m-tnum" style={{ font: "800 20px 'Manrope'", color: stakeNum ? "#fff" : "#3F3F46" }}>{stake || "0"}</span>
              <span style={{ width: 2, height: 20, background: "#00D632", borderRadius: 1 }} />
            </span>
          </div>
          <div style={{ textAlign: "right", flex: "none" }}>
            <span style={{ display: "block", font: "600 10px 'Outfit'", letterSpacing: ".1em", textTransform: "uppercase", color: "#71717a" }}>Payout</span>
            <span className="m-tnum" style={{ font: "800 18px 'Manrope'", color: "#00D632" }}>{fmtAmt(totalReturn)}</span>
          </div>
        </div>
        <div style={{ marginTop: 6, display: "flex", justifyContent: "space-between", font: "500 11.5px 'Outfit'", color: "#71717a" }}>
          <span className="m-tnum">Total stake {fmtAmt(totalStake)}</span>
          <span className="m-tnum">Balance {fmtAmt(bal)}</span>
        </div>

        {/* Place CTA */}
        <button type="button" onClick={place} disabled={blocked}
          style={{ width: "100%", height: 54, borderRadius: 15, border: 0, marginTop: 12, cursor: blocked ? "default" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, font: "800 16px 'Manrope'",
            background: blocked ? "#141417" : "#00D632", color: blocked ? "#52525b" : "#0A0A0A", opacity: (unavailable.length || lineMoved.length || moved.length) ? 0.9 : 1,
            boxShadow: blocked ? "none" : "inset 0 1px 0 rgba(255,255,255,.3),0 10px 24px rgba(0,214,50,.18)" }}>
          {placeLabel}
          {!placing && stakeNum > 0 && !blocked && <span className="m-tnum" style={{ font: "800 14px 'Manrope'", color: "rgba(10,10,10,.7)" }}>· {fmtAmt(totalStake)}</span>}
        </button>
        <p style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, marginTop: 10, font: "500 10px 'Outfit'", color: "#52525b" }}>
          <Icon name="shield-check" size={12} style={{ color: "#52525b" }} /> Odds lock when placed · settles automatically
        </p>
      </div>

      {/* in-sheet keypad — full-bleed tray pinned to the bottom (never the OS keyboard) */}
      <div style={{ flex: "none" }}>
        <Keypad value={stake} onChange={setStake} onMax={setMax} quick={quickChips} isCash={isCash} />
      </div>
    </div>
  );
}
